// core/modals/usermanagement/RoleModal.jsx
import React, { useEffect, useState, useMemo } from "react";
import PropTypes from 'prop-types';
import { userActions } from "../../action/userAction";
import { toast } from "react-toastify";

const RoleModal = ({ mode, role, onClose }) => {
    const [formData, setFormData] = useState({
        role_name: "",
        description: "",
        status: "active",
        permissions: []
    });

    const [permissions, setPermissions] = useState({});
    const [permissionsFlat, setPermissionsFlat] = useState([]);
    const [loading, setLoading] = useState(false);
    const [permissionsLoading, setPermissionsLoading] = useState(false);
    const [errors, setErrors] = useState({});

    // Define module order for better display
    const moduleOrder = useMemo(() => [
        'dashboard',
        'users',
        'roles',
        'products',
        'categories',
        'brands',
        'units',
        'inventory',
        'stock',
        'sales',
        'invoices',
        'sales-returns',
        'purchases',
        'purchase-returns',
        'customers',
        'suppliers',
        'expenses',
        'quotations',
        'tax',
        'barcodes',
        'opening-balance',
        'reports',
        'bank',
        'settings'
    ], []);

    // Load permissions on mount
    useEffect(() => {
        loadPermissions();
    }, []);

    // Load role data if in edit mode
    useEffect(() => {
        if (role && mode === 'edit' && role.id) {
            setFormData({
                role_name: role.role_name || "",
                description: role.description || "",
                status: role.status || "active",
                permissions: []
            });

            // Load role permissions
            loadRolePermissions(role.id);
        }
    }, [role, mode]);

    // Load all permissions
    const loadPermissions = async () => {
        setPermissionsLoading(true);
        try {
            const result = await userActions.getPermissions();
            if (result.success) {
                // Check if result.data is an object with module groups
                if (result.data && typeof result.data === 'object') {
                    // Sort modules according to moduleOrder
                    const sortedData = {};
                    moduleOrder.forEach(module => {
                        if (result.data[module]) {
                            sortedData[module] = result.data[module];
                        }
                    });
                    // Add any remaining modules not in the order
                    Object.keys(result.data).forEach(module => {
                        if (!sortedData[module]) {
                            sortedData[module] = result.data[module];
                        }
                    });
                    setPermissions(sortedData);
                } else {
                    setPermissions({});
                }
                
                // Store flat permissions for easier access
                if (result.flat && Array.isArray(result.flat)) {
                    setPermissionsFlat(result.flat);
                } else {
                    // If flat is not provided, create flat from grouped data
                    const flat = [];
                    if (result.data && typeof result.data === 'object') {
                        Object.values(result.data).forEach(modulePerms => {
                            if (Array.isArray(modulePerms)) {
                                flat.push(...modulePerms);
                            }
                        });
                    }
                    setPermissionsFlat(flat);
                }
            } else {
                toast.error(result.message || 'Failed to load permissions');
                setPermissions({});
                setPermissionsFlat([]);
            }
        } catch (error) {
            console.error('Error loading permissions:', error);
            toast.error('Error loading permissions');
            setPermissions({});
            setPermissionsFlat([]);
        } finally {
            setPermissionsLoading(false);
        }
    };

    // Load role permissions
    const loadRolePermissions = async (roleId) => {
        try {
            const result = await userActions.getRolePermissions(roleId);
            if (result.success) {
                setFormData(prev => ({ ...prev, permissions: result.data || [] }));
            }
        } catch (error) {
            console.error('Error loading role permissions:', error);
            toast.error('Error loading role permissions');
        }
    };

    // Handle input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    // Handle permission toggle
    const handlePermissionToggle = (permissionId) => {
        setFormData(prev => {
            const newPermissions = prev.permissions.includes(permissionId)
                ? prev.permissions.filter(id => id !== permissionId)
                : [...prev.permissions, permissionId];
            return { ...prev, permissions: newPermissions };
        });
    };

    // Handle select all permissions in a module
    const handleSelectAllInModule = (modulePermissions, checked) => {
        const moduleIds = modulePermissions.map(p => p.id);
        setFormData(prev => {
            if (checked) {
                // Add all module permissions not already in the list
                const newPermissions = [...new Set([...prev.permissions, ...moduleIds])];
                return { ...prev, permissions: newPermissions };
            } else {
                // Remove all module permissions
                const newPermissions = prev.permissions.filter(id => !moduleIds.includes(id));
                return { ...prev, permissions: newPermissions };
            }
        });
    };

    // Handle select all permissions
    const handleSelectAllPermissions = (checked) => {
        if (checked) {
            const allPermissionIds = permissionsFlat.map(p => p.id);
            setFormData(prev => ({ ...prev, permissions: [...new Set([...prev.permissions, ...allPermissionIds])] }));
        } else {
            setFormData(prev => ({ ...prev, permissions: [] }));
        }
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};

        if (!formData.role_name.trim()) {
            newErrors.role_name = 'Role name is required';
        }

        if (formData.role_name.length < 2) {
            newErrors.role_name = 'Role name must be at least 2 characters';
        }

        if (formData.role_name.length > 100) {
            newErrors.role_name = 'Role name must be less than 100 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            toast.error('Please fix the errors in the form');
            return;
        }

        setLoading(true);

        const roleData = {
            role_name: formData.role_name.trim(),
            description: formData.description.trim(),
            status: formData.status,
            permissions: formData.permissions
        };

        if (mode === 'edit') {
            roleData.id = role.id;
        }

        try {
            let result;

            if (mode === 'add') {
                result = await userActions.createRole(roleData);
            } else {
                result = await userActions.updateRole(roleData);
            }

            if (result && result.success === true) {
                toast.success(result.message || `Role ${mode === 'add' ? 'created' : 'updated'} successfully`);
                setTimeout(() => {
                    onClose(true);
                }, 1000);
            } else {
                toast.error(result?.message || `Failed to ${mode === 'add' ? 'create' : 'update'} role`);
            }
        } catch (error) {
            console.error(`Error ${mode === 'add' ? 'creating' : 'updating'} role:`, error);
            toast.error(error.message || `Failed to ${mode === 'add' ? 'create' : 'update'} role`);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
    };

    // Check if all permissions in a module are selected
    const areAllModuleSelected = (modulePermissions) => {
        if (!modulePermissions || modulePermissions.length === 0) return false;
        const moduleIds = modulePermissions.map(p => p.id);
        return moduleIds.length > 0 && moduleIds.every(id => formData.permissions.includes(id));
    };

    // Check if any permission in a module is selected (for indeterminate state)
    const isModulePartiallySelected = (modulePermissions) => {
        if (!modulePermissions || modulePermissions.length === 0) return false;
        const moduleIds = modulePermissions.map(p => p.id);
        const anySelected = moduleIds.some(id => formData.permissions.includes(id));
        const allSelected = moduleIds.every(id => formData.permissions.includes(id));
        return anySelected && !allSelected;
    };

    // Check if all permissions are selected
    const areAllPermissionsSelected = () => {
        if (permissionsFlat.length === 0) return false;
        return permissionsFlat.every(p => formData.permissions.includes(p.id));
    };

    // Check if any permission is selected (for indeterminate state on master checkbox)
    const isAnyPermissionSelected = () => {
        return permissionsFlat.some(p => formData.permissions.includes(p.id));
    };

    // Get module display name
    const getModuleDisplayName = (moduleKey) => {
        const displayNames = {
            'dashboard': 'Dashboard',
            'users': 'User Management',
            'roles': 'Role Management',
            'products': 'Product Management',
            'categories': 'Category Management',
            'brands': 'Brand Management',
            'units': 'Unit Management',
            'inventory': 'Inventory Management',
            'stock': 'Stock Management',
            'sales': 'Sales Management',
            'invoices': 'Invoice Management',
            'sales-returns': 'Sales Returns',
            'purchases': 'Purchase Management',
            'purchase-returns': 'Purchase Returns',
            'customers': 'Customer Management',
            'suppliers': 'Supplier Management',
            'expenses': 'Expense Management',
            'quotations': 'Quotation Management',
            'tax': 'Tax Management',
            'barcodes': 'Barcode Management',
            'opening-balance': 'Opening Balance',
            'reports': 'Reports',
            'bank': 'Bank Management',
            'settings': 'System Settings'
        };
        return displayNames[moduleKey] || moduleKey.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            {mode === 'add' ? 'Add New Role' : 'Edit Role'}
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={handleClose} 
                            aria-label="Close"
                            disabled={loading}
                        ></button>
                    </div>

                    <div className="modal-body">
                        <form onSubmit={handleSubmit}>
                            {/* Role Information Section */}
                            <div className="row mb-4">
                                <div className="col-md-6">
                                    <label className="form-label">
                                        Role Name <span className="text-danger">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="role_name"
                                        className={`form-control ${errors.role_name ? 'is-invalid' : ''}`}
                                        value={formData.role_name}
                                        onChange={handleInputChange}
                                        placeholder="Enter role name (e.g., Sales Manager)"
                                        disabled={loading}
                                        autoFocus
                                    />
                                    {errors.role_name && <div className="invalid-feedback">{errors.role_name}</div>}
                                    <small className="text-muted">Role name should be unique and descriptive</small>
                                </div>

                                <div className="col-md-6">
                                    <label className="form-label">Status</label>
                                    <select
                                        name="status"
                                        className="form-select"
                                        value={formData.status}
                                        onChange={handleInputChange}
                                        disabled={loading}
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                    <small className="text-muted">Inactive roles cannot be assigned to users</small>
                                </div>

                                <div className="col-12 mt-3">
                                    <label className="form-label">Description</label>
                                    <textarea
                                        name="description"
                                        className="form-control"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Enter role description (e.g., Manages sales team and targets)"
                                        rows="2"
                                        disabled={loading}
                                    />
                                    <small className="text-muted">Brief description of the roles responsibilities</small>
                                </div>
                            </div>

                            {/* Permissions Section */}
                            <div className="border-top pt-3 mt-2">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="fw-bold mb-0">Permissions</h6>
                                    {permissionsFlat.length > 0 && (
                                        <div className="form-check">
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id="select-all-permissions"
                                                checked={areAllPermissionsSelected()}
                                                ref={input => {
                                                    if (input) {
                                                        input.indeterminate = isAnyPermissionSelected() && !areAllPermissionsSelected();
                                                    }
                                                }}
                                                onChange={(e) => handleSelectAllPermissions(e.target.checked)}
                                                disabled={loading || permissionsLoading}
                                            />
                                            <label className="form-check-label" htmlFor="select-all-permissions">
                                                Select All Permissions
                                            </label>
                                        </div>
                                    )}
                                </div>

                                {permissionsLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading permissions...</span>
                                        </div>
                                        <p className="mt-2 text-muted">Loading permissions...</p>
                                    </div>
                                ) : Object.keys(permissions).length === 0 ? (
                                    <div className="text-center py-5">
                                        <p className="text-muted">No permissions available</p>
                                    </div>
                                ) : (
                                    <div className="permissions-container" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                        {Object.keys(permissions).map(module => (
                                            <div key={module} className="card mb-3">
                                                <div className="card-header bg-light py-2">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <h6 className="mb-0 text-capitalize">
                                                            {getModuleDisplayName(module)}
                                                            <small className="text-muted ms-2" style={{ fontSize: '11px' }}>
                                                                ({permissions[module].length} permissions)
                                                            </small>
                                                        </h6>
                                                        <div className="form-check">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                id={`select-all-${module}`}
                                                                checked={areAllModuleSelected(permissions[module])}
                                                                ref={input => {
                                                                    if (input) {
                                                                        input.indeterminate = isModulePartiallySelected(permissions[module]);
                                                                    }
                                                                }}
                                                                onChange={(e) => handleSelectAllInModule(permissions[module], e.target.checked)}
                                                                disabled={loading}
                                                            />
                                                            <label className="form-check-label" htmlFor={`select-all-${module}`}>
                                                                Select All
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="card-body p-3">
                                                    <div className="row">
                                                        {permissions[module].map(perm => (
                                                            <div key={perm.id} className="col-md-4 col-sm-6 mb-2">
                                                                <div className="form-check">
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input"
                                                                        id={`perm-${perm.id}`}
                                                                        checked={formData.permissions.includes(perm.id)}
                                                                        onChange={() => handlePermissionToggle(perm.id)}
                                                                        disabled={loading}
                                                                    />
                                                                    <label 
                                                                        className="form-check-label" 
                                                                        htmlFor={`perm-${perm.id}`}
                                                                        style={{ cursor: 'pointer' }}
                                                                    >
                                                                        {perm.permission_name}
                                                                        <br />
                                                                        <small className="text-muted" style={{ fontSize: '11px' }}>
                                                                            {perm.permission_slug}
                                                                        </small>
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </form>
                    </div>

                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Processing...
                                </>
                            ) : (
                                mode === 'add' ? 'Create Role' : 'Update Role'
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

RoleModal.propTypes = {
    mode: PropTypes.oneOf(['add', 'edit']).isRequired,
    role: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        role_name: PropTypes.string,
        description: PropTypes.string,
        status: PropTypes.string
    }),
    onClose: PropTypes.func.isRequired
};

RoleModal.defaultProps = {
    role: null
};

export default RoleModal;